import { useMemo } from 'react'
import type {
  Issue,
  UserAnalysis,
  Analysis,
  ListBoxOption,
  Category
} from '../types'

const isBug = (i: Issue | unknown): i is Issue =>
  (i as Issue)?.type === 'Bug'

export function useIssueProcessing({
  analysis,
  userAnalysis,
  userId
}: {
  analysis: Analysis | undefined
  userAnalysis: UserAnalysis | undefined
  userId: string | undefined
}) {
  const result = useMemo(() => {
    if (!analysis || !userAnalysis) {
      return {
        allIssues: [] as Issue[],
        displayedIssues: [] as Issue[],
        removedIssues: [] as Issue[],
        issueTitleMap: new Map<string, string>(),
        uniqEpics: [{ id: 0, name: 'All Epics' }] as ListBoxOption[],
        uniqStatuses: [] as ListBoxOption[],
        issueTypes: [] as ListBoxOption[],
        bugCategories: [] as Category[],
        ticketCategories: [] as Category[],
        changeCategories: [] as Category[]
      }
    }

    const allStartIssues = userAnalysis.start
      .reduce<Issue[][]>((issues, type) => {
        issues.push(type.issues)
        return issues
      }, [])
      .flat(1)

    let allEndIssues = userAnalysis.end
      .reduce<Issue[][]>((issues, type) => {
        issues.push(type.issues)
        return issues
      }, [])
      .flat(1)

    const allEndIssuesConst = allEndIssues

    const unresolvedIssues = userAnalysis.unresolved
      .reduce<Issue[][]>((issues, type) => {
        issues.push(type.issues)
        return issues
      }, [])
      .flat(1)

    allEndIssues = allEndIssues.map(issue => {
      const unresolvedIssue = unresolvedIssues.find(unresolved => Number(unresolved.id) === Number(issue.id))
      if (unresolvedIssue) {
        issue.isPreviouslyReleased = unresolvedIssue.isPreviouslyReleased
      }
      return issue
    })

    const allIssues: Issue[] = []
    allIssues.push(...allEndIssues)

    for (const issue of allStartIssues) {
      if (!allIssues.find(obj => obj.id === issue.id)) {
        allIssues.push(issue)
      }
    }

    const addedDuringSprint =
      (userAnalysis.changes?.addedIssues as Issue[]) ?? []
    for (const issue of addedDuringSprint) {
      if (!allIssues.find(obj => obj.id === issue.id)) {
        allIssues.push(issue)
      }
    }

    for (const issue of allIssues) {
      if (allStartIssues.find(obj => obj.id === issue.id)) {
        issue.startedWith = true
      } else {
        issue.startedWith = false
      }
    }

    const issueTitleMap = new Map<string, string>(
      allIssues.map(issue => [issue.key, issue.summary])
    )

    const uniqEpics: ListBoxOption[] = [{ id: 0, name: 'All Epics' }]
    const processedEpicIds = new Set<number>()
    processedEpicIds.add(0)

    allIssues.forEach(issue => {
      if (issue.epic && issue.epic.id != null) {
        const epicId = Number(issue.epic.id)
        if (!isNaN(epicId) && !processedEpicIds.has(epicId)) {
          uniqEpics.push({
            id: epicId,
            name: issue.epic?.name ?? 'Unnamed Epic'
          })
          processedEpicIds.add(epicId)
        }
      }
    })

    const hasIssuesWithNoEpic = allIssues.some(issue => issue.epic == null)
    if (hasIssuesWithNoEpic && !processedEpicIds.has(-1)) {
      uniqEpics.push({ id: -1, name: 'No Epic' })
    }

    const uniqStatuses: ListBoxOption[] = [
      { id: 0, name: 'All Issue Statuses' },
      { id: 1, name: 'Done' },
      { id: 2, name: 'Not Done' }
    ]

    const issueTypes: ListBoxOption[] = [
      { id: 0, name: 'All Issue Types' }
    ]
    const addedTypeNames = new Set<string>()
    userAnalysis.start.forEach(group => {
      if (group.count > 0 && group.type && !addedTypeNames.has(group.type)) {
        issueTypes.push({ id: group.type, name: group.type })
        addedTypeNames.add(group.type)
      }
    })

    const displayedIssues = structuredClone(allIssues)

    displayedIssues.forEach(issue => {
      const endIssue = allEndIssuesConst.find(e => e.id === issue.id)
      if (endIssue) {
        issue.resolution = endIssue.resolution
        issue.summary = endIssue.summary
        issue.estimate = endIssue.estimate
        issue.type = endIssue.type
        issue.status = endIssue.status
        issue.owner = endIssue.owner
      } else {
        const unresolvedIssue = unresolvedIssues.find(u => u.id === issue.id)
        if (unresolvedIssue) {
          issue.resolution = unresolvedIssue.resolution
          issue.summary = unresolvedIssue.summary
          issue.estimate = unresolvedIssue.estimate
          issue.type = unresolvedIssue.type
          issue.status = unresolvedIssue.status
          issue.owner = unresolvedIssue.owner
          issue.isPreviouslyReleased = unresolvedIssue.isPreviouslyReleased
        } else {
          const addedIssue = addedDuringSprint.find(a => a.id === issue.id)
          if (addedIssue) {
            issue.resolution = addedIssue.resolution
            issue.summary = addedIssue.summary
            issue.estimate = addedIssue.estimate
            issue.type = addedIssue.type
            issue.status = addedIssue.status
            issue.owner = addedIssue.owner
          }
        }
      }
    })

    displayedIssues.sort((a, b) => {
      const aHasEpic = a.epic != null
      const bHasEpic = b.epic != null
      if (aHasEpic && !bHasEpic) return -1
      if (!aHasEpic && bHasEpic) return 1
      if (!aHasEpic && !bHasEpic) return 0
      const aEpicId = Number(a.epic?.id)
      const bEpicId = Number(b.epic?.id)
      if (aEpicId === bEpicId) return 0
      return aEpicId < bEpicId ? -1 : 1
    })

    const changesSafe = userAnalysis.changes ?? {
      addedIssues: [], removedIssues: [], changedIssues: []
    }

    const bugCategories: Category[] = [
      { name: 'Added', issues: changesSafe.addedIssues.filter(isBug) },
      { name: 'Removed', issues: changesSafe.removedIssues.filter(isBug) }
    ]

    const ticketCategories: Category[] = [
      { name: 'Added', issues: changesSafe.addedIssues.filter(i => !isBug(i)) as Issue[] },
      { name: 'Removed', issues: changesSafe.removedIssues.filter(i => !isBug(i)) as Issue[] }
    ]

    const changeCategories: Category[] = [
      { name: 'Changed', issues: changesSafe.changedIssues as Issue[] }
    ]

    let removedIssues: Issue[] = []
    if (analysis.changes && analysis.changes.removedIssues && analysis.changes.removedIssues.length) {
      removedIssues = analysis.changes.removedIssues
      removedIssues = removedIssues.filter(issue =>
        issue.owner && (issue.owner.id === userId || issue.owner.id === userAnalysis.id)
      )
    }

    return {
      displayedIssues,
      allIssues,
      issueTitleMap,
      uniqEpics,
      uniqStatuses,
      issueTypes,
      isBug,
      bugCategories,
      ticketCategories,
      changeCategories,
      removedIssues
    }
  }, [analysis, userAnalysis, userId])

  return result
}

export type { Category }